<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted } from 'vue';
import { useAlert } from 'dashboard/composables';

const MAX_DURATION_SECONDS = 60; // Telegram limit for round videos

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'recordError',
  'cancel',
]);

const videoElement = ref(null);
const mediaStream = ref(null);
const mediaRecorder = ref(null);
const recordedChunks = ref([]);
const isRecording = ref(false);
const elapsedSeconds = ref(0);
const timerInterval = ref(null);
const recordedVideoUrl = ref(null);
const hasRecording = ref(false);

const formatTime = seconds => {
  const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
  const secs = (seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
};

const getSupportedMimeType = () => {
  const types = [
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
};

const startTimer = () => {
  elapsedSeconds.value = 0;
  emit('recorderProgressChanged', formatTime(0));
  timerInterval.value = setInterval(() => {
    elapsedSeconds.value += 1;
    emit('recorderProgressChanged', formatTime(elapsedSeconds.value));
    if (elapsedSeconds.value >= MAX_DURATION_SECONDS) {
      stopRecording();
    }
  }, 1000);
};

const stopTimer = () => {
  if (timerInterval.value) {
    clearInterval(timerInterval.value);
    timerInterval.value = null;
  }
};

const startRecording = async () => {
  try {
    // Prefer square aspect ratio for Telegram round messages
    const constraints = {
      video: {
        width: { ideal: 512 },
        height: { ideal: 512 },
        aspectRatio: { ideal: 1 },
        facingMode: 'user',
      },
      audio: true,
    };

    mediaStream.value = await navigator.mediaDevices.getUserMedia(constraints);

    if (videoElement.value) {
      videoElement.value.srcObject = mediaStream.value;
      videoElement.value.muted = true; // prevent echo during preview
    }

    const mimeType = getSupportedMimeType();
    const options = mimeType ? { mimeType } : {};
    mediaRecorder.value = new MediaRecorder(mediaStream.value, options);

    recordedChunks.value = [];
    mediaRecorder.value.ondataavailable = event => {
      if (event.data && event.data.size > 0) {
        recordedChunks.value.push(event.data);
      }
    };

    mediaRecorder.value.onstop = () => {
      handleStopRecording();
    };

    mediaRecorder.value.start(1000);
    isRecording.value = true;
    startTimer();
  } catch (error) {
    console.error('Video recording error:', error);
    emit('recordError', { error });
  }
};

const stopRecording = () => {
  if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
    mediaRecorder.value.stop();
  }
  stopTimer();
  isRecording.value = false;
};

const cancelRecording = () => {
  if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
    // Prevent onstop from emitting finishRecord
    mediaRecorder.value.onstop = null;
    mediaRecorder.value.stop();
  }
  stopTimer();
  isRecording.value = false;
  hasRecording.value = false;
  recordedChunks.value = [];
  cleanup();
  emit('cancel');
};

const handleStopRecording = () => {
  try {
    if (recordedChunks.value.length === 0) {
      emit('recordError', { error: new Error('Empty recording') });
      return;
    }

    const mimeType = mediaRecorder.value?.mimeType || 'video/webm';
    const videoBlob = new Blob(recordedChunks.value, { type: mimeType });
    const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
    const fileName = `${getUuid()}.${ext}`;
    const file = new File([videoBlob], fileName, { type: mimeType });

    if (recordedVideoUrl.value) URL.revokeObjectURL(recordedVideoUrl.value);
    recordedVideoUrl.value = URL.createObjectURL(videoBlob);

    if (videoElement.value) {
      videoElement.value.srcObject = null;
      videoElement.value.src = recordedVideoUrl.value;
      videoElement.value.muted = false;
      videoElement.value.controls = true;
    }

    hasRecording.value = true;
    emit('finishRecord', {
      name: file.name,
      type: file.type,
      size: file.size,
      duration: elapsedSeconds.value,
      file,
    });
  } catch (error) {
    emit('recordError', { error });
  }
};

const cleanup = () => {
  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach(track => track.stop());
    mediaStream.value = null;
  }
  if (videoElement.value) {
    videoElement.value.srcObject = null;
    videoElement.value.src = '';
  }
};

onMounted(() => {
  startRecording();
});

onUnmounted(() => {
  stopTimer();
  if (recordedVideoUrl.value) {
    URL.revokeObjectURL(recordedVideoUrl.value);
    recordedVideoUrl.value = null;
  }
  cleanup();
});

defineExpose({ stopRecording, cancelRecording });
</script>

<template>
  <div class="video-recorder">
    <div class="video-recorder__preview">
      <video
        ref="videoElement"
        autoplay
        playsinline
        class="video-recorder__video"
      />
    </div>
    <div v-if="isRecording" class="video-recorder__indicator">
      <span class="video-recorder__dot" />
      <span class="video-recorder__time">{{ formatTime(elapsedSeconds) }}</span>
      <span class="video-recorder__hint">/ {{ formatTime(MAX_DURATION_SECONDS) }}</span>
    </div>
    <div class="video-recorder__actions">
      <button
        v-if="isRecording"
        type="button"
        class="button secondary"
        @click="cancelRecording"
      >
        {{ $t('CONVERSATION.REPLYBOX.CANCEL') }}
      </button>
      <button
        v-if="isRecording"
        type="button"
        class="button alert"
        @click="stopRecording"
      >
        {{ $t('CONVERSATION.REPLYBOX.STOP') }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.video-recorder {
  @apply flex flex-col items-center gap-2 w-full p-2;
}

.video-recorder__preview {
  @apply w-full max-w-[320px] aspect-square overflow-hidden bg-n-slate-3;
  border-radius: 50%;
}

.video-recorder__video {
  @apply w-full h-full object-cover;
  border-radius: 50%;
}

.video-recorder__indicator {
  @apply flex items-center gap-2 text-sm font-medium;
}

.video-recorder__dot {
  @apply w-2 h-2 rounded-full bg-n-ruby-9;
  animation: pulse-dot 1s infinite;
}

.video-recorder__time {
  @apply text-n-slate-12 font-mono;
}

.video-recorder__hint {
  @apply text-n-slate-10 font-mono;
}

.video-recorder__actions {
  @apply flex gap-2;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
</style>